/* tslint:disable:member-ordering */
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AmountFormatterService } from '../amount-formatter.service';
import { PaymentPlanTextService } from '../payment-plan-text.service';
import { ZeroRateType } from '../../shared/models/zero-rate-type.model';
import { FormValidator } from '../validators/form-validator';
import { AuthService } from 'src/shared/services/auth.service';
import { PriceVariationComponent } from './price-variation-detail/price-variation.component';
import { PriceSchedule } from 'src/shared/models/price-schedule.model';
import { combineLatest } from 'rxjs';
import { DateRange } from 'src/shared/models/date-range.model';
import { ItemVariation } from '../../shared/models/item-variation.model';
import { SiblingPriceValidatorService } from '../../shared/services/price-validation.service';
import { SeFeFormFieldConfig } from 'se-fe-form-field';
import { SeFeFormFieldMultiselectComponent, SeFeMultiselectItem, SeFeMultiselectOptions } from 'se-fe-form-field-multiselect';
import { CURRENT_ORG_ID } from '../../shared/services/organization-id.service';
import { ReferenceCodeService } from '../../shared/services/reference-code.service';
import { SeFeThemesLoader } from 'se-fe-themes';

@Component({
  selector: 'app-payment-amount-options',
  templateUrl: './payment-amount-options.component.html',
  styleUrls: ['./payment-amount-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PaymentAmountOptionsComponent implements OnChanges, OnInit, AfterViewInit, AfterViewChecked {
  @Input() formValidator: FormValidator;
  @Input() itemForm: FormGroup;
  @Input() showSaleItemType?: string;
  @Input() showTaxCode: boolean;
  @Input() itemVariation: ItemVariation;

  disabled = true;
  maximumUpfrontAmount: string;
  showSaleItemTypeFlag = false;
  upfrontAmountInputFocused = false;
  isAdmin = false;
  upfrontAmountOption = 'fullAmount';
  zeroRateTypeList: ZeroRateType[] = [
    { value: 'background_screen', label: 'Safety: NCSI Background Screening' },
    { value: 'abuse_prevention_system', label: 'Safety: Abuse Prevention System (APS) Training' },
  ];
  priceVariations: PriceSchedule[] = [];
  priceVariationDates: DateRange[] = [];
  priceVariationNames: string[] = [];
  priceVariationNamesSubscriptions = [];
  savedPriceSchedules: Partial<PriceSchedule>[] = [];
  priceVariationsInitialized = false;

  //#region - reference code config -
  displayReferenceCode = true;
  referenceCodeReady = false;
  referenceCodeOptions: SeFeMultiselectOptions = {
    sections: [
      {
        options: [{ text: '--', value: '' }],
      },
    ],
  };
  referenceCodeQueryValidators = [Validators.maxLength(100)];
  referenceCodeConfig: SeFeFormFieldConfig = {
    label: '',
    errorMessages: {
      maxlength: 'Max length exceeded.',
    },
  };
  priceFieldConfig: SeFeFormFieldConfig = {
    label: 'Price',
    errorMessages: {
      required: 'Enter price',
      min: 'Enter $0.00 or more',
      pattern: 'Enter a valid price',
      UnqualifiedForUpfrontAmount: 'Enter $25 or more to use with the selected upfront payment option',
    },
  };
  skuFieldConfig: SeFeFormFieldConfig = {
    label: 'SKU',
    errorMessages: {
      maxlength: 'SKU cannot exceed 64 characters',
    },
  };
  taxCodeFieldConfig: SeFeFormFieldConfig = {
    label: 'Tax Code',
    errorMessages: {
      maxlength: 'Enter up to 25 letters or numbers (no spaces)',
      pattern: 'Enter up to 25 letters or numbers (no spaces)',
    },
  };
  upfrontAmountFieldConfig: SeFeFormFieldConfig = {
    label: 'Partial amount due up front',
    errorMessages: {
      pattern: 'Enter a valid amount',
      min: 'Enter $0.00 or more',
      maximum: 'Enter a valid upfront amount',
      required: 'Enter a valid upfront amount',
    },
  };
  upfrontAmountOptions = [
    { label: 'Full price due up front', value: 'fullAmount' },
    {
      label: 'Partial amount due up front',
      value: 'partialAmount',
      disabled: true,
      help: 'Price must be $25.00 or more to qualify',
    },
    {
      label: 'Nothing due up front',
      value: 'zeroAmount',
      disabled: true,
      help: 'Price must be $25.00 or more to qualify',
    },
  ];
  @ViewChild('referenceCodeField') referenceCodeField: SeFeFormFieldMultiselectComponent;
  //#endregion - reference code config -

  @ViewChildren(PriceVariationComponent) priceVariationComponents: QueryList<PriceVariationComponent>;

  get referenceCode(): any {
    const selectedCode = this.itemForm.get('referenceCode').value;
    const numericRegex = /^[0-9]+$/;
    const option = this.referenceCodeOptions.sections[0].options.find((o) => o.value === selectedCode);
    if (!selectedCode) {
      return null;
    } else if (numericRegex.test(option.value)) {
      return { id: option.value };
    } else {
      return { title: option.text, tenant_id: this.organizationId };
    }
  }

  constructor(
    private detector: ChangeDetectorRef,
    public authService: AuthService,
    private amountFormatter: AmountFormatterService,
    private paymentPlanTextService: PaymentPlanTextService,
    private elementRef: ElementRef,
    private siblingPriceValidatorService: SiblingPriceValidatorService,
    @Inject(CURRENT_ORG_ID) public organizationId: string,
    private referenceCodeService: ReferenceCodeService
  ) {
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com',
    });
  }

  loadReferenceCodes(): void {
    this.referenceCodeService.list(this.organizationId).subscribe((response) => {
      const options: SeFeMultiselectItem[] = response.result.map((code) => {
        return { text: code.title, value: code.id };
      });
      this.referenceCodeOptions.sections[0].options.push(...options);
      this.detector.markForCheck();
      this.detector.detectChanges();

      this.bindReferenceCode();
    });
  }

  bindReferenceCode(): void {
    if (this.itemVariation && this.itemVariation.reference_code) {
      const referenceCode = this.itemVariation.reference_code as string;
      const option = this.referenceCodeOptions.sections[0].options.find((o) => o.text === referenceCode);
      const currentValue = this.itemForm.get('referenceCode').value;
      if (option && option.value !== currentValue) {
        this.itemForm.get('referenceCode').setValue(option.value, { emitEvent: false });
      }
    }
    this.referenceCodeReady = true;
  }

  ngOnInit() {
    this.addFormControls();
    this.setPaymentPlanText(true);
    this.setShowSaleItemTypeFlag();
    this.getAdminStatus();
    this.loadReferenceCodes();
  }

  ngAfterViewInit() {
    this.priceVariationComponents.changes.subscribe(() => {
      this.setPriceVariationDates();
      this.setPriceVariationNames();
      this.subscribeToPriceVariationDateChanges();
      this.subscribeToPriceVariationNameChanges();
      this.setPaymentPlanText();
    });
  }

  private async getAdminStatus(): Promise<void> {
    this.isAdmin = await this.authService.isCurrentUserAdmin();
    this.detector.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.itemVariation.id) {
      this.bindFormControls();
      if (!this.priceControl()) {
        this.addPriceControl();
      }
      if (this.itemVariation.zero_rate_type) {
        this.setShowSaleItemTypeFlag();
      }
      this.setupUpfrontAmountItemVariation();
      this.setPartialAmount();
      this.setPaymentPlanText();
      this.setPriceVariations();
      this.siblingPriceValidatorService.initializePrices(
        this.priceVariations.map((priceVariation) => priceVariation.price_cents)
      );
    }
  }

  ngAfterViewChecked() {
    this.setComponentStyles();
  }

  public handlePriceVariationButtonClick(): void {
    this.priceVariations.push({
      pricing_name: '',
      price_cents: null,
      upfront_amount_cents: null,
      start_date: '',
      end_date: '',
      show: true,
    });
  }

  public handlePriceVariationRemove(priceVariation: PriceSchedule, index: number): void {
    priceVariation.show = false;
    this.priceVariations.splice(index, 1);
    this.siblingPriceValidatorService.removePrices(index);
  }

  bindFormControls(): void {
    this.itemForm.get('zeroRateType').setValue(this.itemVariation.zero_rate_type);
    this.itemForm.get('price').setValue(this.itemVariation.price);
    this.itemForm.get('upfrontAmount').setValue(this.itemVariation.upfront_amount);
    this.itemForm.get('sku').setValue(this.itemVariation.sku);
    this.itemForm.get('taxCode').setValue(this.itemVariation.tax_code);
    if (this.referenceCodeReady) {
      this.bindReferenceCode();
    }
  }

  addPriceControl(): void {
    this.itemForm.addControl(
      'price',
      new FormControl(this.itemVariation.price, [
        Validators.required,
        Validators.min(0),
        Validators.pattern(/^(\$)?(([0-9,]+)?([0-9]\.)?(\.[0-9]+)?)$/),
        this.validatePriceWithUpfrontAmount(),
      ])
    );
  }

  private addFormControls() {
    this.itemForm.addControl('zeroRateType', new FormControl(this.itemVariation.zero_rate_type));
    this.addPriceControl();
    this.itemForm.addControl(
      'upfrontAmount',
      new FormControl('', [
        Validators.min(0),
        Validators.pattern(/^(\$)?(([0-9,]+)?([0-9]\.)?(\.[0-9]+)?)$/),
        this.validateUpfrontAmount(),
      ])
    );
    this.itemForm.addControl('sku', new FormControl(this.itemVariation.sku, [Validators.maxLength(64)]));
    this.itemForm.addControl(
      'taxCode',
      new FormControl(this.itemVariation.tax_code, [Validators.maxLength(25), Validators.pattern(/^[a-zA-Z0-9]*$/)])
    );

    if (!this.itemForm.get('referenceCode')) {
      this.itemForm.addControl('referenceCode', new FormControl(''), { emitEvent: false });
    }
    this.formValidator.updateForm(this.itemForm);
  }

  private zeroRateTypeControl(): AbstractControl {
    return this.itemForm.get('zeroRateType');
  }

  private priceControl(): AbstractControl {
    return this.itemForm.get('price');
  }

  private upfrontAmountControl(): AbstractControl {
    return this.itemForm.get('upfrontAmount');
  }

  public setShowSaleItemTypeFlag(): void {
    if (this.showSaleItemType === 'true') {
      this.showSaleItemTypeFlag = true;
    }
  }

  public isPartialAmount(): boolean {
    return this.upfrontAmountOption === 'partialAmount';
  }

  public isFullAmount(): boolean {
    return this.upfrontAmountOption === 'fullAmount';
  }

  public isZeroAmount(): boolean {
    return this.upfrontAmountOption === 'zeroAmount';
  }

  public isUpfrontAmountReadOnly(): boolean {
    return this.disabled && !this.isPartialAmount();
  }

  private setPartialAmount() {
    if (this.itemVariation.id && this.isPartialAmount()) {
      this.upfrontAmountControl().setValue(this.itemVariation.upfront_amount);
      this.detector.detectChanges();
    }
  }

  private validatePriceWithUpfrontAmount() {
    return (control: AbstractControl) => {
      const price = control.value;
      if (!price) {
        return;
      }
      if (!this.isFullAmount() && this.amountFormatter.sanitizePrice(price) < 25) {
        return { UnqualifiedForUpfrontAmount: true };
      }
      return null;
    };
  }

  private validateUpfrontAmount(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!this.itemForm) {
        return;
      }
      const upfrontAmount = this.amountFormatter.sanitizePrice(control.value);
      const price = this.amountFormatter.sanitizePrice(this.priceControl().value);
      if (this.isPartialAmount()) {
        if (!upfrontAmount) {
          return { required: true };
        } else if (price >= 25 && upfrontAmount > price - 10) {
          return { maximum: true };
        }
      }
      return null;
    };
  }

  private setUpfrontAmountOption(): void {
    const price = this.itemVariation.price_cents;
    const upfrontAmount = this.itemVariation.upfront_amount_cents;
    if (price !== 0 && upfrontAmount === 0) {
      this.upfrontAmountOption = 'zeroAmount';
    } else if (price === upfrontAmount || !upfrontAmount) {
      this.upfrontAmountOption = 'fullAmount';
    } else if (price > upfrontAmount) {
      this.upfrontAmountOption = 'partialAmount';
    }
  }

  private setupUpfrontAmountItemVariation(): void {
    if (this.itemVariation.id) {
      this.setUpfrontAmountOption();
      this.setDisabled();
      this.setMaximumAllowedUpfrontAmount(this.itemVariation.price_cents / 100);
    }
  }

  public onUpfrontAmountOptionChanged(value: string): void {
    if (this.disabled) {
      return;
    }
    this.upfrontAmountOption = value;
    if (!this.isPartialAmount()) {
      this.upfrontAmountControl().setValue(null);
    }
    this.clearPriceValidationError();
    this.setDisabled();
    this.detector.detectChanges();
    this.setPaymentPlanText();
  }

  public setPaymentPlanText(defaultState: boolean = false): void {
    const priceVariations =
      this.priceVariationComponents?.map((priceVariationComponent) => priceVariationComponent.formGroup) || [];
    this.paymentPlanTextService.paymentPlanTextSubject.next({
      itemForm: this.itemForm,
      upfrontAmountOption: this.upfrontAmountOption,
      defaultState: defaultState,
      priceVariations,
    });
  }

  public setDisabled(): void {
    let price = this.itemVariation.price;
    if (this.itemForm) {
      price = this.priceControl().valid ? this.priceControl().value : null;
    }
    if (!price) {
      return;
    }
    this.disabled = this.amountFormatter.sanitizePrice(price) < 25 && this.isFullAmount();
    this.setUpfrontAmountOptions();
  }

  public setAdditionalSettings(): void {
    this.itemVariation.zero_rate_type = this.zeroRateTypeControl().value;
  }

  public setUpfrontAmount(): void {
    if (this.isFullAmount()) {
      this.itemVariation.upfront_amount = this.priceControl().value;
    } else if (this.isZeroAmount()) {
      this.itemVariation.upfront_amount = '0.00';
    } else if (this.isPartialAmount()) {
      this.itemVariation.upfront_amount = this.upfrontAmountControl().value;
    }
    this.itemVariation.upfront_amount_cents = Math.round(
      this.amountFormatter.sanitizePrice(this.itemVariation.upfront_amount) * 100
    );
  }

  public resetPaymentAmountOptions(): void {
    if (this.itemVariation.id) {
      this.setupUpfrontAmountItemVariation();
      // this.addFormControls();
      this.itemForm.reset({ emitEvent: false });
      this.bindFormControls();
      this.setPartialAmount();
    } else {
      this.setDefaults();
      this.itemForm.reset();
      this.setPaymentPlanText(true);
    }
  }

  public preSubmitCheck(): void {
    if (this.isZeroAmount()) {
      this.itemForm.controls['upfrontAmount'].setErrors(null);
    } else {
      this.upfrontAmountControl().updateValueAndValidity();
      this.upfrontAmountControl().markAsDirty();
    }
    this.setPaymentPlanText();
    this.priceControl().markAsDirty();
  }

  private setDefaults(): void {
    this.disabled = true;
    this.upfrontAmountOption = 'fullAmount';
    this.maximumUpfrontAmount = undefined;
    this.setUpfrontAmountOptions();
  }

  private setUpfrontAmountOptions(): void {
    this.upfrontAmountOptions = [
      { label: 'Full price due up front', value: 'fullAmount' },
      {
        label: 'Partial amount due up front',
        value: 'partialAmount',
        disabled: this.disabled,
        help: this.disabled ? 'Price must be $25.00 or more to qualify' : undefined,
      },
      {
        label: 'Nothing due up front',
        value: 'zeroAmount',
        disabled: this.disabled,
        help: this.disabled ? 'Price must be $25.00 or more to qualify' : undefined,
      },
    ];
  }

  public onPriceChange(): void {
    const amount = this.formatAmount('price');
    this.setMaximumAllowedUpfrontAmount(amount);
    this.formatAmount('upfrontAmount');
  }

  public onUpfrontAmountChange(): void {
    this.upfrontAmountInputFocused = false;
    this.formatAmount('upfrontAmount');
    this.setPaymentPlanText();
  }

  private formatAmount(formInput: string): number {
    this.detector.detectChanges();
    const amountControl = this.itemForm.get(formInput);
    let amount = amountControl.value;

    if (!amount) {
      return;
    }
    amount = this.amountFormatter.sanitizePrice(amount);
    if (isNaN(amount)) {
      return;
    }
    amountControl.setValue(this.amountFormatter.formattedAmount(amount));
    return amount;
  }

  private setMaximumAllowedUpfrontAmount(price: number): void {
    const amount = price - 10;
    if (amount >= 10) {
      this.maximumUpfrontAmount = this.amountFormatter.formattedAmount(amount);
    }
  }

  public setUpfrontAmountFocus(): void {
    this.upfrontAmountInputFocused = !this.disabled;
  }

  private clearPriceValidationError(): void {
    if (this.isFullAmount() && this.priceControl().hasError('UnqualifiedForUpfrontAmount')) {
      delete this.priceControl().errors['UnqualifiedForUpfrontAmount'];
      this.priceControl().updateValueAndValidity();
    }
  }

  private setComponentStyles(): void {
    const addPriceVariationButton: HTMLElement =
      this.elementRef.nativeElement.querySelector('.add-price-variation-button')?.firstChild?.firstChild;
    if (addPriceVariationButton) {
      addPriceVariationButton.style.textTransform = 'none';
    }
    const saleItemTypeDropdown =
      this.elementRef.nativeElement.querySelector('.sale-item-type-dropdown')?.firstChild?.firstChild;
    if (saleItemTypeDropdown) {
      saleItemTypeDropdown.style.color = 'inherit';
    }
  }

  private setPriceVariationDates(): void {
    this.priceVariationDates = this.priceVariationComponents.map((priceVariationComponent, index) => {
      const formGroup = priceVariationComponent.formGroup;
      const start_date = formGroup?.get('start_date').value;
      const end_date = formGroup?.get('end_date').value;
      priceVariationComponent.componentIndex = index;
      if (start_date && end_date) {
        return { start_date, end_date };
      } else {
        return null;
      }
    });
  }

  private setPriceVariationNames(): void {
    this.priceVariationNames = this.priceVariationComponents.map((priceVariationComponent) => {
      return priceVariationComponent.formGroup.get('pricing_name').value;
    });
  }

  private subscribeToPriceVariationDateChanges(): void {
    this.priceVariationComponents.forEach((priceVariationComponent, index) => {
      const startDateChanges$ = priceVariationComponent.formGroup.get('start_date').valueChanges;
      const endDateChanges$ = priceVariationComponent.formGroup.get('end_date').valueChanges;
      combineLatest([startDateChanges$, endDateChanges$]).subscribe(([start_date, end_date]) => {
        if (
          priceVariationComponent.formGroup.get('start_date').valid &&
          priceVariationComponent.formGroup.get('end_date').valid
        ) {
          const newPriceVariationDates = this.priceVariationComponents.map((component) => {
            return {
              start_date: component.formGroup.get('start_date').value,
              end_date: component.formGroup.get('end_date').value,
            };
          });
          newPriceVariationDates[index] = { start_date, end_date };
          this.priceVariationDates = newPriceVariationDates;
        }
      });
    });
  }

  private subscribeToPriceVariationNameChanges(): void {
    this.priceVariationNamesSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.priceVariationNamesSubscriptions = [];
    this.priceVariationComponents.forEach((priceVariationComponent, index) => {
      this.priceVariationNamesSubscriptions.push(
        priceVariationComponent.formGroup.get('pricing_name').valueChanges.subscribe((pricing_name) => {
          if (this.priceVariationNames[index] !== pricing_name) {
            const updatedNames = [...this.priceVariationNames];
            updatedNames[priceVariationComponent.componentIndex] = pricing_name;
            this.priceVariationNames = updatedNames;
          }
        })
      );
    });
  }

  private setPriceVariations() {
    this.priceVariations = [];
    (<any>this.itemVariation).price_variations?.forEach((priceSchedule) => {
      this.priceVariations.push({
        pricing_name: priceSchedule.pricing_name,
        price_cents: priceSchedule.price,
        upfront_amount_cents: priceSchedule.upfront_amount,
        price_schedule_id: priceSchedule.id,
        start_date: priceSchedule.start_date,
        end_date: priceSchedule.end_date,
        show: true,
        is_editing: true,
      });
    });
    if (!this.priceVariationsInitialized && this.priceVariations.length) {
      this.savedPriceSchedules = this.priceVariations.map((priceVariation) => {
        return {
          price_schedule_id: priceVariation.price_schedule_id,
          pricing_name: priceVariation.pricing_name,
        };
      });
      this.priceVariationsInitialized = true;
    }
  }
}
